import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, catchError, throwError, iif } from 'rxjs';
import { switchMap, map, retryWhen, concatMap, delay, distinctUntilChanged } from 'rxjs/operators';
import { collection, collectionData, deleteField, doc, docData, DocumentReference, Firestore, orderBy, query, runTransaction, updateDoc, where } from '@angular/fire/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';

import { OperationWithId, OpStatus } from '@innersight-os/shared/firestore-interfaces';
import { combineLatestOrEmptyArray } from '@innersight-os/shared/util-rxjs'
import { UsersService } from '../users/users.service';
import { ActionsService } from '../actions/actions.service';
import { AssignmentsService } from '../assignments/assignments.service';
import { TeamsService } from '../teams/teams.service';
import { DocumentData } from '@firebase/firestore-types';

@Injectable({
  providedIn: 'root'
})
export class OperationsService {
  constructor(
    private db: Firestore,
    private actions: ActionsService,
    private assignments: AssignmentsService,
    private teams: TeamsService,
    private users: UsersService,
    private snackBar: MatSnackBar,
  ) {}

  allOperations$ = collectionData(
    query<OperationWithId, DocumentData>(
      // eslint-able-next-line @typescript-eslint/no-explicit-any
      collection(this.db, 'operations') as any,
      orderBy('createdAt', 'desc'),
    ),
    {idField: 'id'},
  )

  myAssignedOperations$ = this.assignments.myAssignedOperationIds$.pipe(
    switchMap(ids =>
      combineLatestOrEmptyArray(ids.map(this.getOperation))
    ),
    retryWhen(errors=>errors.pipe(
      concatMap((err: Error, i) =>
        // Executes a conditional Observable depending on the result of the first argument
        iif(
          () => {
            i<3 && console.log(`myAssignedOperations$ error ${err.message} retry #`,i)
            return i > 3
          },
          // If the condition is true we throw the error (the last error)
          throwError(() => err),
          // Otherwise we pipe this back into our stream and delay the retry
          of(err).pipe(delay(2000))
        )
      )
    ))
  )

  myManagedOperations$ = this.teams.myManagedTeams$.pipe(
    switchMap(teams => {
      if(!teams.length) return of([])

      const teamIds = teams.map(t => t.id)
      // 'in' operator only works for up to 10 managed teams
      // use combineLatest if we need >10
      // TODO: consider making into operator like libs/shared/firestore-observables/src/lib/array-query.ts
      const perChunk = 10 // items per chunk
      const chunkedTeamIds: string[][] = teamIds.reduce((resultArray, item, index) => {
        const chunkIndex = Math.floor(index/perChunk)
        if(!resultArray[chunkIndex]) {
          resultArray[chunkIndex] = [] // start a new chunk
        }
        resultArray[chunkIndex].push(item)
        return resultArray
      }, [])
      // console.log(chunkedTeamIds); // result: [['teamId0',...,'teamId9'], ['teamId10',...'teamId19'],...]
      const chunkOps = chunkedTeamIds.map(teamIdsChunk =>
        collectionData(
          query<OperationWithId, DocumentData>(
            // eslint-able-next-line @typescript-eslint/no-explicit-any
            collection(this.db, 'operations') as any,
            where('team', 'in', teamIdsChunk),
            orderBy('createdAt', 'desc')
          ),
          {idField: 'id'},
        )
      )

      return combineLatest(chunkOps).pipe(map(ops=>ops.flat()))
    }),
    catchError((err: Error) => {
      console.error(err)
      const message = '[myManagedOperations$] error when fetching operations, returning empty list. Please contact support.'
      console.error(message)
      this.snackBar.open(message, 'dismiss')
      return of([])
    }),
  )

  myAssignedAndManagedOperations$ = combineLatest([
    this.myAssignedOperations$,
    this.myManagedOperations$,
  ]).pipe(
    map(([assigned, managed]) => {
      const managedIds = managed.map(op => op.id)
      const assignedNotManaged = assigned.filter(op => !managedIds.includes(op.id))
      return [...managed, ...assignedNotManaged].sort((a, b) => a.createdAt > b.createdAt ? -1 : 1)
    })
  )

  myOperations$: Observable<OperationWithId[]> = this.users.me$.pipe(
    switchMap(me => {
      if(me?.roles?.admin)       return this.allOperations$
      if(me?.roles?.manager)
        if(me?.roles?.segmenter) return this.myAssignedAndManagedOperations$
        else                     return this.myManagedOperations$
      if(me?.roles?.segmenter)   return this.myAssignedOperations$
      return of(null)
    }),
  )

  // TODO: remove escape hatch 'statusChanged' event now actions are stable?
  async updateOperationStatus(operationId: string, status: OpStatus) {
    try {
      return await runTransaction(this.db, async transaction => {
        const opRef = doc(this.db, 'operations', operationId)
        transaction.update(opRef, {status})
        this.actions.recordAction(undefined, operationId, {type: 'statusChanged', message: `to ${status}`}, transaction)
      })
    } catch(err){
      console.error('transaction failed - did not update operation status', err);
      throw err
    }
  }

  async recordOperationAction(operationId: string, type: Parameters<ActionsService['recordAction']>[2]['type'], message?: string) {
    try {
      await runTransaction(this.db, async transaction => {
        this.actions.recordAction(undefined, operationId, {type, message}, transaction)
      })
    } catch (err) {
      console.error('failed to record operation action', err)
      throw err
    }
  }

  getAgreementOperations(agreementId: string): Observable<OperationWithId[]> {
    return collectionData(
      query(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collection(this.db, 'operations') as any,
        where('agreement', '==', agreementId),
      ),
      {idField: 'id'},
    ) as Observable<OperationWithId[]>
  }

  async attributeOperationToAgreement(operationId: string, agreementId: string | null) {
    try {
      return await updateDoc(
        doc(this.db, 'operations', operationId),
        {agreement: agreementId ?? deleteField()},
      )
    } catch(err) {
      console.error('attribution failed', err);
    }
  }


  async updateOperationField(operationId: string, field: string, value: unknown) {
    try {
      await updateDoc(doc(this.db, `operations/${operationId}`), field, value)
    } catch(err){
      console.error(`update failed - did not update operation field ${field}`, err);
      throw err
    }
  }

  async updateOperationFields2(operationId: string, field1: string, value1: unknown, field2: string, value2: unknown) {
    try {
      await updateDoc(doc(this.db, `operations/${operationId}`), field1, value1, field2, value2)
    } catch(err){
      console.error(`update failed - did not update operation fields ${field1} ${field2}`, err);
      throw err
    }
  }

  getOperation = (id: string): Observable<OperationWithId> =>
    docData<OperationWithId>(
      doc(this.db, 'operations', id) as DocumentReference<OperationWithId>,
      {idField: 'id'},
    )

  getWarningForOperation(operationId: string): Observable<string> {
    return this.getOperation(operationId).pipe(
      map(op => op.warning),
      distinctUntilChanged(),
    )
  }

  getWarningKeysForOperation(operationId: string): Observable<string[]> {
    return this.getOperation(operationId).pipe(
      map(op => op.warningKeys),
      distinctUntilChanged(),
    )
  }

}
